import React,{useState,useEffect,useCallback} from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faStar, faUsers, faClock, faPlay, 
  faCheckCircle, faDatabase, faRobot, 
  faBrain, faChartLine, faProjectDiagram,
  faLaptopCode, faCertificate, faBriefcase
} from '@fortawesome/free-solid-svg-icons';
import './HeroSection.css';

const AiProfessional = () => {
   const [showModal, setShowModal] = useState(false);
    const [formCourseName, setFormCourseName] = useState('AI Professional');
    const [formData, setFormData] = useState({
      fullName: '',
      mobileNumber: '',
      email: '',
    });
  
    const openModal = () => setShowModal(true);
    const closeModal = useCallback(() => {
      setShowModal(false);
      setFormData({ fullName: '', mobileNumber: '', email: '' });
    }, []);
  
    useEffect(() => {
      const handleEsc = (e) => {
        if (e.key === 'Escape') closeModal();
      };
      window.addEventListener('keydown', handleEsc);
      return () => window.removeEventListener('keydown', handleEsc);
    }, [closeModal]);
  
    const handleInputChange = (e) => {
      const { name, value } = e.target;
      if (name === 'mobileNumber') {
        const digitsOnly = value.replace(/\D/g, '');
        if (digitsOnly.length <= 10) {
          setFormData(prev => ({ ...prev, [name]: digitsOnly }));
        }
      } else {
        setFormData(prev => ({ ...prev, [name]: value }));
      }
    };
  
    const handleSubmit = async (e) => {
      e.preventDefault();
      const { fullName, mobileNumber, email } = formData;
  
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
      if (!fullName || !emailRegex.test(email) || mobileNumber.length !== 10) {
        alert('Please fill in valid details.');
        return;
      }
  
      try {
        const response = await fetch('https://api.dikshasea.in/api/enrollments', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...formData, course: formCourseName }),
        });
  
        const result = await response.json();
        if (response.ok) {
          alert('Enrollment successful!');
          closeModal();
        } else {
          alert(result.message || 'Submission failed');
        }
      } catch (err) {
        console.error(err);
        alert('Error submitting form');
      }
    };
  
  return (
    <>
      <section className="hero-section">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <span className="badge bg-primary bg-opacity-10 text-white mb-3">New AI Program</span>
              <h1 className="hero-title">
                Become an <span className="hero-highlight">AI Professional</span>
              </h1>
              <p className="hero-subtitle">
                Master cutting-edge AI technologies with our industry-aligned program. 
                Develop real-world skills in machine learning, deep learning, and AI deployment 
                through hands-on projects and expert mentorship.
              </p>
              
              <div className="course-meta">
                <div className="meta-item">
                  <div className="meta-icon">
                    <FontAwesomeIcon icon={faStar} />
                  </div>
                  <div>
                    <div className="fw-bold">4.9/5.0</div>
                    <small className="text-muted">(4,500+ reviews)</small>
                  </div>
                </div>
                <div className="meta-item">
                  <div className="meta-icon">
                    <FontAwesomeIcon icon={faUsers} />
                  </div>
                  <div>
                    <div className="fw-bold">20,000+</div>
                    <small className="text-muted">Professionals</small>
                  </div>
                </div>
                <div className="meta-item">
                  <div className="meta-icon">
                    <FontAwesomeIcon icon={faClock} />
                  </div>
                  <div>
                    <div className="fw-bold">80 hours</div>
                    <small className="text-muted">Hands-on Content</small>
                  </div>
                </div>
              </div>
              
              <button className="btn btn-primary btn-lg px-4" onClick={openModal}>
                  Enroll Now
                </button>
            </div>
            <div className="col-lg-6 mt-5 mt-lg-0">
              <div className="ratio ratio-16x9">
                <img 
                  src="subcourses-images/ai-professional.jpg" 
                  alt="AI Professional Program" 
                  className="img-fluid rounded-3 shadow"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="container">
        <div className="course-details">
          <div className="course-content">
            {/* About Course */}
            <section className="about-course">
              <h2 className="section-title">About This Program</h2>
              <p>Our AI Professional program is designed to transform you into a job-ready AI specialist. You'll gain comprehensive knowledge of machine learning algorithms, neural networks, and AI deployment strategies used by top tech companies.</p>
              <p>Through industry projects and case studies, you'll build a professional portfolio demonstrating your ability to solve real business problems with AI solutions.</p>
            </section>
            
            {/* Curriculum */}
            <section className="curriculum">
              <h2 className="section-title">Program Curriculum</h2>
              
              <div className="curriculum-item">
                <h3><FontAwesomeIcon icon={faRobot} className="me-2" /> Module 1: AI & Machine Learning Foundations</h3>
                <p>Build strong fundamentals in artificial intelligence and predictive modeling</p>
                <div className="topics">
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Supervised vs unsupervised learning</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Feature engineering techniques</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Model evaluation metrics</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Ethical AI principles</div>
                </div>
              </div>
              
              <div className="curriculum-item">
                <h3><FontAwesomeIcon icon={faBrain} className="me-2" /> Module 2: Deep Learning Architectures</h3>
                <p>Master neural networks and advanced AI models</p>
                <div className="topics">
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Neural network fundamentals</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> CNN, RNN, and Transformer models</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Transfer learning techniques</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Hyperparameter optimization</div>
                </div>
              </div>
              
              <div className="curriculum-item">
                <h3><FontAwesomeIcon icon={faChartLine} className="me-2" /> Module 3: Natural Language Processing</h3>
                <p>Develop expertise in text processing and language AI</p>
                <div className="topics">
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Text preprocessing pipelines</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Sentiment analysis systems</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Transformer architectures</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Large Language Models (LLMs)</div>
                </div>
              </div>
              
              <div className="curriculum-item">
                <h3><FontAwesomeIcon icon={faDatabase} className="me-2" /> Module 4: Computer Vision Systems</h3>
                <p>Build intelligent visual recognition applications</p>
                <div className="topics">
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Image classification models</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Object detection frameworks</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Image segmentation techniques</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Video analysis applications</div>
                </div>
              </div>
              
              <div className="curriculum-item">
                <h3><FontAwesomeIcon icon={faProjectDiagram} className="me-2" /> Module 5: AI Deployment & Scaling</h3>
                <p>Learn to productionize AI solutions at scale</p>
                <div className="topics">
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Model deployment pipelines</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> AI system monitoring</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Cloud AI services</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> AI project lifecycle management</div>
                </div>
              </div>
            </section>
          </div>  
          
          <div className="course-sidebar">
            <div className="info-item">
              <div className="info-icon">
                <FontAwesomeIcon icon={faClock} />
              </div>
              <div className="info-content">
                <h4>Duration</h4>
                <p>6 Months (15-20 hrs/week)</p>
              </div>
            </div>
            
            <div className="info-item">
              <div className="info-icon">
                <FontAwesomeIcon icon={faCertificate} />
              </div>
              <div className="info-content">
                <h4>Level</h4>
                <p>Intermediate to Advanced</p>
              </div>
            </div>
            
            <div className="info-item">
              <div className="info-icon">
                <FontAwesomeIcon icon={faLaptopCode} />
              </div>
              <div className="info-content">
                <h4>Projects</h4>
                <p>5 Industry Projects</p>
              </div>
            </div>
            
            <div className="info-item">
              <div className="info-icon">
                <FontAwesomeIcon icon={faUsers} />
              </div>
              <div className="info-content">
                <h4>Prerequisites</h4>
                <p>Python, Linear Algebra</p>
              </div>
            </div>
            
            <div className="info-item">
              <div className="info-icon">
                <FontAwesomeIcon icon={faBriefcase} />
              </div>
              <div className="info-content">
                <h4>Outcomes</h4>
                <p>AI Professional Certification</p>
              </div>
            </div>
          <button className="btn btn-primary btn-lg px-4" onClick={openModal}>
                  Enroll Now
                </button>
          </div>
        </div>
      </div>
      {/* MODAL */}
      {showModal && (
        <div
          className="modal d-block fade show"
          style={{ background: 'rgba(0,0,0,0.5)' }}
          onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content p-4 d-flex flex-column flex-md-row">
              <div className="w-100 w-md-50 mb-3 mb-md-0">
                <img
                  src="/img/enroll.jpg"
                  alt="Enrollment"
                  className="img-fluid h-100"
                  style={{ objectFit: 'cover', width: '100%' }}
                />
              </div>
              <div className="w-100 w-md-50 ps-md-4">
                <h6 className="text-primary">Course:</h6>
                <h4 className="fw-bold">AI Professional</h4>
                <form onSubmit={handleSubmit} noValidate>
                  <input
                    className="form-control mb-3"
                    type="text"
                    name="fullName"
                    placeholder="Full Name"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    required
                  />
                  <input
                    className="form-control mb-3"
                    type="text"
                    name="mobileNumber"
                    placeholder="Mobile Number"
                    value={formData.mobileNumber}
                    onChange={handleInputChange}
                    required
                    maxLength={10}
                  />
                  <input
                    className="form-control mb-3"
                    type="email"
                    name="email"
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                  <div className="d-flex justify-content-end">
                    <button type="button" className="btn btn-outline-dark me-2" onClick={closeModal}>
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary">Submit</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AiProfessional;